import { Component, OnInit, ViewChild } from '@angular/core';
import * as Highcharts from 'highcharts';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService, SmartIrrigationService } from 'src/api/services';
import { AutomatedSmartIrrigationSchedule, DataAnalysisTimeSeriesValue, SmartIrrigationRootNodeDto, SmartIrrigationSoilMoistureProbe } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

export interface DailyReading {
  date: string;
  moisture: number | null;
  temperature: number | null;
  conductivity: number | null;
  status: 'alert' | 'warning' | 'normal';
}

@Component({
  selector: 'app-smart-irrigation-sensor-trend',
  templateUrl: './sensor-trend.component.html',
  styleUrls: ['./sensor-trend.component.scss']
})
export class SmartIrrigationSensorTrendComponent implements OnInit {

  nodeId: number;
  siteUri: string;
  node: SmartIrrigationRootNodeDto;
  probe: SmartIrrigationSoilMoistureProbe;
  /** Id of the Park node that contains the current site, used for breadcrumb navigation. */
  parkId: number | null = null;
  parkName: string | null = null;

  Highcharts = Highcharts;
  chartConstructor = 'chart';

  loading = true;
  noData = false;
  lastRefresh: Date | null = null;
  paramName = 'Sensor';
  reportPeriod = '';

  moistureCurrent = '--';
  moistureChange = '--';
  moistureRange = '--';
  overallTrend = '--';
  overallTrendNote = '';

  dailyReadings: DailyReading[] = [];
  soilMoistureProbes: SmartIrrigationSoilMoistureProbe[] = [];
  moistureChartOptions: Highcharts.Options;
  temperatureChartOptions: Highcharts.Options | null = null;
  conductivityChartOptions: Highcharts.Options | null = null;

  /** True when no automated schedules with thresholds are configured for this controller. */
  noSchedule = false;
  automatedSchedules: AutomatedSmartIrrigationSchedule[] = [];
  scheduleLoading = false;

  @ViewChild('scheduleModal') scheduleModal: ModalDirective;

  private controllerDeviceEui: string;
  /** Expanded threshold step-line points and zones from controller schedules. */
  private scheduleThreshold: { points: [number, number][]; zones: Highcharts.SeriesZonesOptionsObject[] } = { points: [], zones: [] };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private dataService: DataService,
    private toaster: ToastrService
  ) {}

  ngOnInit(): void {
    this.nodeId = +this.route.snapshot.paramMap.get('nodeId')!;
    this.siteUri = this.route.snapshot.queryParams['siteUri'];
    this.controllerDeviceEui = this.route.snapshot.queryParams['controllerDeviceEui'];
    this.paramName = this.route.snapshot.queryParams['name'] || 'Sensor';

    const from = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
    const to = new Date();
    this.reportPeriod = `${from.toLocaleDateString()} – ${to.toLocaleDateString()}`;

    this.buildEmptyChart();
    this.findUriAndLoad();
  }

  findUriAndLoad(): void {
    // Load node and schedules concurrently.
    forkJoin({
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId),
      node: this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: this.nodeId })
    }).subscribe(({ dashboard, node }) => {
      this.node = node;
      this.parkId = node.parentNodeId ?? null;
      if (node.parentNodeId) {
        this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: node.parentNodeId }).subscribe(parentNode => {
          this.parkName = parentNode.name || null;
        });
      }
            const controller = dashboard.irrigationControllers?.find(c => c.deviceEui === this.controllerDeviceEui);
      this.soilMoistureProbes = [...(controller?.soilMoistureProbes || [])]
        .sort((a, b) => this.getProbeName(a).localeCompare(this.getProbeName(b)));

      const now = Date.now();
      const windowStart = now - 14 * 24 * 3600 * 1000;
      
      this.probe = controller.soilMoistureProbes.find(p => p.siteUri === this.siteUri);
      if (this.probe) {
        this.paramName = this.getProbeName(this.probe);
      }

      this.irrigation.SmartIrrigationGetAutomatedSchedule({ customerId: this.app.customerId, deviceEui: this.probe.deviceEui }).subscribe(controllerSchedules => {
          console.log('controllerSchedules ' + this.probe.deviceEui, controllerSchedules);
          this.masterEditSchedules = controllerSchedules;

        if (controllerSchedules.length > 0) {
          this.scheduleThreshold = this.buildThresholdSeriesData(controllerSchedules, windowStart, now);
          this.noSchedule = this.scheduleThreshold.points.length === 0;
        } else {
          this.scheduleThreshold = { points: [], zones: [] };
          this.noSchedule = true;
        }

        if (!this.probe) {
          this.loading = false;
          this.noData = true;
          return;
        }
        //const p = dashboard.irrigationControllers?.find(c => c.siteUri === probe?.uri);

        this.loadProbeSeries(this.probe);
      });
    }, () => { this.loading = false; this.noData = true; });
  }

  private getProbeName(probe: SmartIrrigationSoilMoistureProbe): string {
    return probe.description || probe.name || probe.deviceEui;
  }

  navigateToSensor(probe: SmartIrrigationSoilMoistureProbe): void {
    if (!probe.siteUri) {
      return;
    }

    this.router.navigate(['/smart-irrigation/sensor-trend/' + this.nodeId], {
      queryParams: {
        siteUri: probe.siteUri,
        controllerDeviceEui: this.controllerDeviceEui,
        name: this.getProbeName(probe)
      }
    }).then(navigated => {
      if (navigated) {
        window.location.reload();
      }
    });
  }

  daysOfWeek: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  getDayAsNumber(day: string):  0 | 1 | 2 | 3 | 4 | 5 | 6 {
    return this.daysOfWeek.indexOf(day) as 0 | 1 | 2 | 3 | 4 | 5 | 6;
  }

  masterEditSchedules: AutomatedSmartIrrigationSchedule[];

  onScheduleChange(schedule: any){
    console.log('schedule changed', schedule);  
    //for (let schedule of schedules) {
      console.log(schedule);
      // if schedule.id is null, create new schedule
      if (schedule.id != null) {
        const master = this.masterEditSchedules.find(s => s.id == schedule.id);
        this.irrigation.SmartIrrigationUpdateAutomatedSchedule({
          customerId: this.app.customerId,
          Id: schedule.id,
          ControllerDeviceEui: master.controllerDeviceEui,
          SoilMoistureProbeDeviceEui: this.probe.deviceEui,
          DayOfWeek: this.getDayAsNumber(schedule.day),
          StartTime: `${schedule.startHour.toString().padStart(2,'0')}:00`,
          EndTime: `${(schedule.endHour+1).toString().padStart(2,'0')}:00`,
          IrrigationCutoff: schedule.cutoff
        }).subscribe(r => {
          console.log('updated schedule', r);
          //schedule.id = r.id;
        });
      } else {
        debugger;
        this.irrigation.SmartIrrigationAddAutomatedSchedule({
          customerId: this.app.customerId,
          ControllerDeviceEui: this.controllerDeviceEui,
          SoilMoistureProbeDeviceEui: this.probe.deviceEui,
          DayOfWeek: this.getDayAsNumber(schedule.day),
          StartTime: `${schedule.startHour.toString().padStart(2,'0')}:00`,
          EndTime: `${(schedule.endHour+1).toString().padStart(2,'0')}:00`,
          IrrigationCutoff: schedule.cutoff
        }).subscribe(r => {
          console.log('added schedule', r);
          schedule.id = r.id;
        });
      }
    //}
  }

  onScheduleAdd(schedule: any){
    console.log('schedule add', schedule);  
    //for (let schedule of schedules) {
      console.log(schedule);
      // if schedule.id is null, create new schedule
      if (schedule.id != null) {
        const master = this.masterEditSchedules.find(s => s.id == schedule.id);
        this.irrigation.SmartIrrigationUpdateAutomatedSchedule({
          customerId: this.app.customerId,
          Id: schedule.id,
          ControllerDeviceEui: master.controllerDeviceEui,
          SoilMoistureProbeDeviceEui: this.probe.deviceEui,
          DayOfWeek: this.getDayAsNumber(schedule.day),
          StartTime: `${schedule.startHour.toString().padStart(2,'0')}:00`,
          EndTime: `${(schedule.endHour+1).toString().padStart(2,'0')}:00`,
          IrrigationCutoff: schedule.cutoff
        }).subscribe(r => {
          console.log('updated schedule', r);
          this.toaster.success('Schedule updated');
          //schedule.id = r.id;
        }, error => {
          this.toaster.error('Error updating schedule');
        });
      } else {
        this.irrigation.SmartIrrigationAddAutomatedSchedule({
          customerId: this.app.customerId,
          ControllerDeviceEui: this.controllerDeviceEui,
          SoilMoistureProbeDeviceEui: this.probe.deviceEui,
          DayOfWeek: this.getDayAsNumber(schedule.day),
          StartTime: `${schedule.startHour.toString().padStart(2,'0')}:00`,
          EndTime: `${(schedule.endHour+1).toString().padStart(2,'0')}:00`,
          IrrigationCutoff: schedule.cutoff
        }).subscribe(r => {
          console.log('added schedule', r);
          schedule.id = r.id;
          this.masterEditSchedules.push(r);
          this.toaster.success('Schedule added');
        }, error => {
          this.toaster.error('Error adding schedule');
        });
    }
  }

  openScheduleEditor(): void {
    this.scheduleLoading = true;
    this.irrigation.SmartIrrigationGetAutomatedSchedule(
        {customerId:this.app.customerId, deviceEui:this.probe.deviceEui}).subscribe(schedules => {
      this.automatedSchedules = (schedules || []);
      console.log("Schedules for editing " + this.probe.deviceEui, this.automatedSchedules);
      this.scheduleLoading = false;
      this.scheduleModal.show();
    }, () => {
      this.scheduleLoading = false;
    });
  }

  onScheduleDelete(schedule: any){
    console.log('schedule delete', schedule);      
    // if schedule.id is null, create new schedule
    if (schedule.id != null) {
      const master = this.masterEditSchedules.find(s => s.id == schedule.id);
      this.irrigation.SmartIrrigationDeleteSchedule({
        customerId: this.app.customerId,
        scheduleId: schedule.id
      }).subscribe(r => {
        console.log('delete schedule', r);
        //schedule.id = r.id;
        this.toaster.success('Schedule deleted');
      }, error => {
        this.toaster.error('Error deleting schedule');
      });
    } 
  }

  onClearSchedules(): void {
    if (!this.probe || !this.probe.deviceEui) {
      this.toaster.error('No soil moisture device selected');
      return;
    }

    if (!window.confirm('Remove all automated schedules for this soil moisture device?')) {
      return;
    }

    this.irrigation.SmartIrrigationClearAutomatedSchedule({
      customerId: this.app.customerId,
      deviceEui: this.probe.deviceEui
    }).subscribe(() => {
      this.automatedSchedules = [];
      this.masterEditSchedules = [];
      this.toaster.success('All schedules cleared');
    }, () => {
      this.toaster.error('Error clearing schedules');
    });
  }

  private loadProbeSeries(probe: SmartIrrigationSoilMoistureProbe): void {
    const requests: {
      moisture: Observable<DataAnalysisTimeSeriesValue[]>;
      temperature: Observable<DataAnalysisTimeSeriesValue[]>;
      conductivity: Observable<DataAnalysisTimeSeriesValue[]>;
    } = {
      moisture: this.fetchSeries(probe.soilMoistureDataUri),
      temperature: this.fetchSeries(probe.temperatureDataUri),
      conductivity: this.fetchSeries(probe.conductivityDataUri)
    };

    forkJoin(requests).subscribe(({ moisture, temperature, conductivity }) => {
      const hasMoisture = moisture.length > 0;
      const hasTemperature = temperature.length > 0;
      const hasConductivity = conductivity.length > 0;

      this.noData = !hasMoisture && !hasTemperature && !hasConductivity;

      if (hasMoisture) {
        this.buildMetrics(moisture);
        this.buildChart(moisture);
      } else {
        this.buildEmptyChart();
      }

      this.buildDailyReadings(moisture, temperature, conductivity);

      this.temperatureChartOptions = hasTemperature
        ? this.buildSimpleTrendChart(temperature, 'Temperature \\u2014 14-Day History', '\\u00B0C', 'temperature')
        : null;

      this.conductivityChartOptions = hasConductivity
        ? this.buildSimpleTrendChart(conductivity, 'Conductivity (EC) \\u2014 14-Day History', 'µS/cm', 'conductivity')
        : null;

      this.loading = false;
      this.lastRefresh = new Date();
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private fetchSeries(uri?: string): Observable<DataAnalysisTimeSeriesValue[]> {
    if (!uri) {
      return of([]);
    }

    return this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        forceEmbed: true,
        index: 0,
        period: '-14d:1d',
        type: 'TimeSeries',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        timeSeries: { single: { variable: { uri } } }
      }
    }).pipe(map(result => result?.values || []));
  }

  private buildMetrics(values: DataAnalysisTimeSeriesValue[]): void {
    const nums = values.map(v => v.value).filter(v => v != null);
    if (nums.length === 0) return;

    const first = nums[nums.length - 1];
    const last = nums[0];
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const change = last - first;

    this.moistureCurrent = last.toFixed(1);
    this.moistureRange = `${min.toFixed(1)} – ${max.toFixed(1)}`;
    this.moistureChange = `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    this.overallTrend = change < -5 ? 'Declining' : change > 5 ? 'Improving' : 'Stable';
    this.overallTrendNote = change < -5
      ? 'Moisture trending downward over the 14-day period.'
      : change > 5
        ? 'Moisture trending upward over the 14-day period.'
        : 'Moisture levels stable over the 14-day period.';

  }

  private buildDailyReadings(
    moistureValues: DataAnalysisTimeSeriesValue[],
    temperatureValues: DataAnalysisTimeSeriesValue[],
    conductivityValues: DataAnalysisTimeSeriesValue[]
  ): void {
    const byDay = new Map<string, { moisture: number | null; temperature: number | null; conductivity: number | null }>();

    const upsert = (values: DataAnalysisTimeSeriesValue[], key: 'moisture' | 'temperature' | 'conductivity') => {
      for (const value of values) {
        if (value.value == null) {
          continue;
        }
        const day = new Date(value.timeStamp).toLocaleDateString();
        const existing = byDay.get(day) || { moisture: null, temperature: null, conductivity: null };
        existing[key] = value.value;
        byDay.set(day, existing);
      }
    };

    upsert(moistureValues, 'moisture');
    upsert(temperatureValues, 'temperature');
    upsert(conductivityValues, 'conductivity');

    this.dailyReadings = Array.from(byDay.entries())
      .map(([date, reading]) => {
        const moisture = reading.moisture;
        return {
          date,
          moisture,
          temperature: reading.temperature,
          conductivity: reading.conductivity,
          status: (moisture != null && moisture < 15
            ? 'alert'
            : moisture != null && moisture < 20
              ? 'warning'
              : 'normal') as DailyReading['status']
        };
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  private buildEmptyChart(): void {
    this.moistureChartOptions = this.chartOptions([], this.scheduleThreshold);
  }

  private buildChart(values: DataAnalysisTimeSeriesValue[]): void {
    // Sort chronologically and convert to [timestamp, value] pairs for a datetime axis.
    const data = this.buildChartData(values);
    this.moistureChartOptions = this.chartOptions(data, this.scheduleThreshold);
  }

  private buildSimpleTrendChart(
    values: DataAnalysisTimeSeriesValue[],
    title: string,
    yAxisTitle: string,
    parameter: 'temperature' | 'conductivity'
  ): Highcharts.Options {
    const data = this.buildChartData(values);

    const now = Date.now();
    const maxX = this.getMidnightTonightMs();
    const minX = maxX - 14 * 24 * 3600 * 1000;

    return {
      chart: { type: 'area', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: minX,
        max: maxX,
        tickInterval: 24 * 3600 * 1000,
        gridLineWidth: 1,
        plotLines: [this.currentTimePlotLine(now)],
        labels: { format: '{value:%d %b}', rotation: -30 }
      },
      yAxis: {
        title: { text: yAxisTitle }
      },
      tooltip: {
        shared: true,
        valueSuffix: ` ${yAxisTitle}`,
        xDateFormat: '%d %b %Y %H:%M'
      },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: { area: { lineWidth: 2, marker: { enabled: false, radius: 3 }, fillOpacity: 0.15, connectNulls: false } },
      series: [
        {
          name: title,
          type: 'area',
          data,
          color: '#38a169',
          zoneAxis: 'y',
          zones: this.getParameterZones(parameter),
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [[0, 'rgba(56,161,105,0.3)'], [1, 'rgba(56,161,105,0)']] as any
          }
        } as Highcharts.SeriesAreaOptions
      ]
    };
  }

  private buildChartData(values: DataAnalysisTimeSeriesValue[]): [number, number | null][] {
    const sorted = [...values].sort((a, b) =>
      new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
    );
    const maxGapMs = 12 * 60 * 60 * 1000;
    const data: [number, number | null][] = [];

    sorted.forEach((value, index) => {
      const timestamp = new Date(value.timeStamp).getTime();
      const previousTimestamp = index > 0 ? new Date(sorted[index - 1].timeStamp).getTime() : null;
      if (previousTimestamp != null && timestamp - previousTimestamp > maxGapMs) {
        data.push([previousTimestamp + 1, null]);
      }
      data.push([timestamp, value.value ?? null]);
    });

    return data;
  }

  public getTemperatureStatus(value: number | null): 'red' | 'amber' | 'green' {
    return value == null ? 'green' : value < 5 || value > 40 ? 'red' : value < 10 || value > 35 ? 'amber' : 'green';
  }

  public getConductivityStatus(value: number | null): 'red' | 'amber' | 'green' {
    return value == null ? 'green' : value > 4000 ? 'red' : value > 2000 ? 'amber' : 'green';
  }

  private getParameterZones(parameter: 'temperature' | 'conductivity'): Highcharts.SeriesZonesOptionsObject[] {
    return parameter === 'temperature'
      ? [
          { value: 5, color: '#e53e3e' },
          { value: 10, color: '#d97706' },
          { value: 35, color: '#38a169' },
          { value: 40, color: '#d97706' },
          { color: '#e53e3e' }
        ]
      : [
          { value: 2000, color: '#38a169' },
          { value: 4000, color: '#d97706' },
          { color: '#e53e3e' }
        ];
  }

  private chartOptions(
    data: [number, number | null][],
    threshold: { points: [number, number][]; zones: Highcharts.SeriesZonesOptionsObject[] }
  ): Highcharts.Options {
    // Determine x-axis extent so threshold lines span the full range.
    const ms24h = 24 * 3600 * 1000;
    const now = Date.now();
    const maxX = this.getMidnightTonightMs();
    const minX = maxX - 14 * ms24h;

    const series: Highcharts.SeriesOptionsType[] = [
      { name: 'Moisture', type: 'area', data, color: '#0d6efd', fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, 'rgba(13,110,253,0.3)'], [1, 'rgba(13,110,253,0)']] as any }, zIndex: 3 } as any
    ];

    if (threshold.points.length > 0) {
      // Fill below scheduled (solid) threshold sections only.
      series.push({
        name: 'Do Not Irrigate Fill',
        type: 'area',
        data: threshold.points as any,
        step: 'left',
        zoneAxis: 'x',
        zones: threshold.zones.map(z => ({
          value: z.value,
          color: 'rgba(0,0,0,0)',
          fillColor: z.dashStyle === 'Solid'
            ? {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [[0, 'rgba(220,53,69,0.25)'], [1, 'rgba(220,53,69,0.0)']]
              }
            : 'rgba(0,0,0,0)',
          dashStyle: z.dashStyle
        })) as any,
        lineWidth: 0,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 1
      } as any);

      series.push({
        name: 'Do Not Irrigate',
        type: 'line',
        data: threshold.points as any,
        step: 'left',
        zoneAxis: 'x',
        zones: threshold.zones,
        lineWidth: 1.5,
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 5
      } as any);
    }
    
    const thresholdValues = threshold.points
      .map(([, value]) => value)
      .filter(value => Number.isFinite(value));
    const moistureValues = data
      .map(([, value]) => value)
      .filter((value): value is number => value != null && Number.isFinite(value));
    const axisValues = [...thresholdValues, ...moistureValues];

    let yAxisMin = 0;
    let yAxisMax: number | undefined;
    if (axisValues.length > 0) {
      const minValue = Math.min(...axisValues);
      const maxValue = Math.max(...axisValues);
      const span = Math.max(1, maxValue - minValue);
      const pad = Math.max(2, span * 0.1);

      yAxisMin = Math.max(0, minValue - pad);
      yAxisMax = Math.min(100, maxValue + pad);
    }

    return {
      chart: { type: 'area', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: minX,
        max: maxX,
        // One gridline per day exactly at midnight.
        tickInterval: ms24h,
        gridLineWidth: 1,
        plotLines: [this.currentTimePlotLine(now)],
        labels: { format: '{value:%d %b}', rotation: -30 }
      },
      yAxis: {
        min: yAxisMin,
        max: yAxisMax,
        title: { text: '% VMC' }
      },
      tooltip: { shared: true, valueSuffix: ' % VMC', xDateFormat: '%d %b %Y %H:%M' },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: { area: { lineWidth: 2, marker: { enabled: false, radius: 3 }, fillOpacity: 0.15 } },
      series
    };
  }

  private currentTimePlotLine(now: number): Highcharts.XAxisPlotLinesOptions {
    return {
      value: now,
      color: '#6c757d',
      dashStyle: 'ShortDash',
      width: 2,
      zIndex: 10,
      label: {
        text: 'Now',
        rotation: 0,
        align: 'left',
        x: 4,
        y: 14,
        style: {
          color: '#6c757d',
          fontSize: '10px'
        }
      }
    };
  }

  private getMidnightTonightMs(): number {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  /**
   * Expands weekly schedules into a 14-day step-line with solid segments during scheduled windows
   * and dashed segments during gaps.
   */
  private buildThresholdSeriesData(
    schedules: AutomatedSmartIrrigationSchedule[],
    windowStart: number,
    windowEnd: number
  ): { points: [number, number][]; zones: Highcharts.SeriesZonesOptionsObject[] } {
    const ms24h = 24 * 3600 * 1000;
    const solidColor = '#dc3545';
    const gapColor = 'rgba(220,53,69,0.45)';
    const intervals: { startMs: number; endMs: number; cutoff: number }[] = [];
    let dayStart = this.startOfDay(windowStart);
    for (const s of schedules) {
      console.log('Day of week', s.dayOfWeek, 'start', s.startTime, 'end', s.endTime, 'cutoff', s.irrigationCutoff);
    }
    while (dayStart < windowEnd) {
      const jsDay = new Date(dayStart).getDay(); // 0=Sunday...6=Saturday
      console.log('Processing day', new Date(dayStart).toDateString(), 'JS day', jsDay);
      for (const s of schedules) {
        if (!s.startTime || !s.endTime) { continue; }
        if (s.dayOfWeek != null && s.dayOfWeek !== jsDay) { continue; }
        console.log('Matched day')
        const startMs = dayStart + this.timeStringToMs(s.startTime);
        let endMs = dayStart + this.timeStringToMs(s.endTime);
        if (endMs <= startMs) { endMs += ms24h; }
        if (endMs > windowStart && startMs < windowEnd) {
          intervals.push({
            startMs: Math.max(startMs, windowStart),
            endMs: Math.min(endMs, windowEnd),
            cutoff: s.irrigationCutoff
          });
          //console.log(`Schedule ${s.name} on ${new Date(dayStart).toDateString()} from ${s.startTime} to ${s.endTime} with cutoff ${s.irrigationCutoff}%`);
        }
      }
      dayStart += ms24h;
    }

    if (intervals.length === 0) {
      return { points: [], zones: [] };
    }

    intervals.sort((a, b) => a.startMs - b.startMs);

    const points: [number, number][] = [];
    const zones: Highcharts.SeriesZonesOptionsObject[] = [];
    const firstCutoff = intervals[0].cutoff;

    if (intervals[0].startMs > windowStart) {
      points.push([windowStart, firstCutoff]);
      zones.push({ value: intervals[0].startMs, color: gapColor, dashStyle: 'ShortDash' });
    } else {
      points.push([windowStart, intervals[0].cutoff]);
    }

    for (let i = 0; i < intervals.length; i++) {
      const iv = intervals[i];
      const next = intervals[i + 1];

      points.push([iv.startMs, iv.cutoff]);
      zones.push({ value: iv.endMs, color: solidColor, dashStyle: 'Solid' });
      points.push([iv.endMs, iv.cutoff]);

      if (next) {
        if (iv.endMs < next.startMs) {
          zones.push({ value: next.startMs, color: gapColor, dashStyle: 'ShortDash' });
        }
      } else if (iv.endMs < windowEnd) {
        points.push([windowEnd, iv.cutoff]);
        zones.push({ color: gapColor, dashStyle: 'ShortDash' });
      }
    }

    return { points, zones };
  }

  private startOfDay(ms: number): number {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  private timeStringToMs(time: string): number {
    const [h, m, s] = time.split(':').map(Number);
    return ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
  }

  goBack(): void {
    if (this.controllerDeviceEui) {
      this.router.navigate(['/smart-irrigation/site'], { queryParams: { deviceEui: this.controllerDeviceEui } });
    } else {
      this.router.navigate(['/smart-irrigation/regions']);
    }
  }
}
